/**
 * Les libellés et couleurs de l'activité, partagés par les deux écrans d'usage.
 *
 * L'écran Statistiques (une organisation) et le panneau Admin (tout le parc) affichent les
 * mêmes pastilles à partir des mêmes données. Ces tables y vivaient en double, aux mêmes
 * valeurs : un module commun évite qu'une correction n'en touche qu'une moitié — le genre
 * de dérive qui fait dire que les deux écrans « ne comptent pas pareil ».
 *
 * Les noms d'export gardent la forme des constantes d'origine, pour que les deux écrans les
 * importent sans réécrire leurs usages.
 */

export const COLORS = {
  members: '#3b82f6',    // Bleu
  clients: '#8b5cf6',    // Violet
  DASHBOARD: '#3b82f6',
  AI_ANALYSIS: '#8b5cf6',
  TOOLS: '#22c55e',
  MCP: '#f59e0b',        // Orange
  LOGIN: '#6b7280',
  // Volontairement discret : une présence n'est pas un acte, elle ne doit pas attirer l'œil
  // autant qu'une analyse ou une requête MCP.
  SESSION: '#cbd5e1'
};

export const ACTIVITY_LABELS = {
  DASHBOARD: 'Dashboard',
  AI_ANALYSIS: 'Analyse IA',
  TOOLS: 'Outils',
  MCP: 'MCP',
  LOGIN: 'Connexion',
  // « Présence » et non « Connexion » : la personne avait déjà une session ouverte — le jeton
  // vaut sept jours — et n'a pas eu à se ré-authentifier. Dire « connexion » laisserait croire
  // à une authentification qui n'a pas eu lieu.
  SESSION: 'Présence'
};

export const DASHBOARD_MODEL_LABELS = {
  // Dashboards Google Ads
  'global': 'Global',
  'comparatif': 'Comparatif',
  'insights-ia': 'Insights IA',
  'termes-recherche': 'Termes recherche',
  'google-ads': 'Google Ads',
  'performance-max': 'Performance Max',
  'performance-max-shopping': 'PMax Shopping',
  'campagnes-produits': 'Campagnes Produits',
  'shopping': 'Shopping',
  'cross-sales': 'Ventes croisées',
  'consolide': 'Consolidé',
  'competition': 'Concurrence',
  // Dashboards Microsoft Ads
  'microsoft-ads': 'Microsoft Ads',
  'microsoft-ads-global': 'Microsoft Ads',
  'microsoft-ads-previous': 'Microsoft Ads',
  'microsoft-ads-insights': 'Microsoft Ads Insights',
  'microsoft-ads-termes-recherche': 'Microsoft Ads Termes',
  'microsoft-ads-groupes-annonces': 'Microsoft Ads Groupes',
  'microsoft-ads-reseaux': 'Microsoft Ads Réseaux',
  'campagnes-termes-recherche': 'Campagnes Termes recherche',
  // Dashboards Meta Ads
  'facebook-ads': 'Meta Ads',
  // Autres régies
  'tiktok-ads': 'TikTok Ads',
  'pinterest-ads': 'Pinterest Ads',
  'chatgpt-ads': 'ChatGPT Ads',
  // Dashboards Analytics
  'matomo': 'Matomo',
  'piano': 'Piano',
  'ga4': 'Google Analytics 4',
  // Affiliation et produits
  'awin': 'Awin',
  'insights-produits': 'Insights produits',
  // Outils
  'bid-optimizer': 'Optimiseur enchères',
  'device-bid-adjustments': 'Ajustements appareils',
  'keyword-opportunities': 'Opportunités mots-clés',
  'keyword-planner': 'Générateur de mots-clés',
  'microsoft-negative-keywords': 'Termes à exclure (Microsoft)',
  'feed-diagnostics': 'Diagnostic flux',
  'feed-optimizer': 'Optimiseur flux',
  'sea-seo-overlap': 'Chevauchement SEA/SEO',
  'product-labeling': 'Étiquetage produits',
  'campaign-objectives': 'Objectifs campagnes',
  'diagnostic': 'Diagnostic Google Ads',
  'diagnostic-seo': 'Diagnostic SEO',
  'backlinks': 'Backlinks',
  'serp-tracking': 'Suivi SERP',
  'competitor-insights': 'Annonces concurrentes',
  // Le journal de ciblage : les relevés « Pourquoi je vois cette pub ? » saisis à la main.
  // Tracé à l'écriture seule — consulter la synthèse n'est pas un acte.
  'competitor-ciblage': 'Ciblage concurrents',
  'ai-visibility': 'Visibilité IA',
  // Analyse IA : le marqueur posé quand l'analyse a échoué (`analyzeDashboard.js`).
  'error': 'Analyse en échec'
};

export const MCP_SOURCE_COLORS = {
  GOOGLE_ADS: '#4285f4',
  META_ADS: '#1877f2',
  MICROSOFT_ADS: '#00a4ef',
  MATOMO: '#3152a0',
  GOOGLE_ANALYTICS_4: '#e37400',
  NOTES: '#22c55e',
  COMPETITOR_ADS: '#ec4899',
  PAGESPEED: '#7c3aed'
};

export const MCP_SOURCE_LABELS = {
  GOOGLE_ADS: 'Google Ads',
  META_ADS: 'Meta Ads',
  MICROSOFT_ADS: 'Microsoft Ads',
  MATOMO: 'Matomo',
  GOOGLE_ANALYTICS_4: 'Google Analytics',
  NOTES: 'Notes',
  COMPETITOR_ADS: 'Annonces concurrentes',
  PAGESPEED: 'PageSpeed'
};

/**
 * La pastille qui suit le badge « MCP » dans le détail des actions.
 *
 * **Toutes les requêtes MCP ne nomment pas une source.** L'intercepteur `journalMcp` journalise
 * les échecs de routes qui n'en portent aucune — `page-speed`, les mutations Microsoft ou Meta —
 * et écrit alors `sourceType: null`, à dessein : le défaut « MCP » y affichait « MCP MCP ».
 * L'écran, lui, rendait une pastille grise **vide**, qui n'apprenait rien à personne.
 *
 * L'opération, elle, est toujours connue (`requestType`, le chemin de la route) : à défaut de
 * source, c'est elle qu'on montre. Et rien du tout plutôt qu'une pastille sans texte.
 *
 * @param {{sourceType?: string, requestType?: string}} mcpDetails
 * @returns {{texte: string, couleur: string}|null} null quand il n'y a rien à afficher
 */
export const pastilleMcp = (mcpDetails) => {
  if (!mcpDetails) return null;

  const { sourceType, requestType } = mcpDetails;

  if (sourceType) {
    return {
      texte: MCP_SOURCE_LABELS[sourceType] || sourceType,
      couleur: MCP_SOURCE_COLORS[sourceType] || '#6b7280'
    };
  }

  if (requestType) {
    // « microsoft-ads/mutate » se lit mieux en « microsoft ads › mutate ».
    return {
      texte: String(requestType).replace(/\//g, ' › ').replace(/[-_]/g, ' '),
      couleur: '#6b7280'
    };
  }

  return null;
};

/**
 * La personne a-t-elle été vue après sa dernière action enregistrée ?
 *
 * Les fenêtres de détail — Statistiques et Admin — listent les **actes** et écartent la
 * présence, écrite au plus toutes les cinq minutes pour toute requête authentifiée. Les écrans
 * qui les ouvrent affichent, eux, une « dernière activité » qui la compte. Les deux se
 * contredisaient : un relevé montre un compte vu le 1er octobre dont la dernière action remonte
 * au 15 septembre — seize jours, sans rien à l'écran pour l'expliquer.
 *
 * Sans aucune action, une présence suffit à mériter la mention : c'est le cas le plus
 * déroutant — un compteur à zéro face à une « dernière connexion » du jour.
 *
 * **Le critère est le jour, pas l'instant.** Quelqu'un qui navigue laisse en permanence une
 * présence postérieure à son dernier acte : mesuré, trente-six minutes d'écart sur un compte
 * actif. Mentionner cela à chaque ouverture banaliserait la ligne jusqu'à la rendre invisible,
 * alors qu'elle doit expliquer une seule chose — deux écrans qui affichent deux dates
 * différentes, et c'est la date, donc le jour, qui se lit à l'écran.
 *
 * Partagée par les deux écrans : une seconde copie aurait divergé, et c'est précisément d'une
 * divergence entre deux lectures de la même donnée que vient le défaut qu'elle corrige.
 */
export const presencePlusRecente = (detail) => {
  if (!detail?.dernierePresence) return false;

  const derniereAction = detail.actions?.[0]?.loginAt;
  if (!derniereAction) return true;

  const jour = (date) => new Date(date).toLocaleDateString('fr-CA'); // AAAA-MM-JJ, local
  return jour(detail.dernierePresence) > jour(derniereAction);
};
